import { RefCallback, useCallback, useRef, useState } from 'react';
import {
	MAX_ORDERBOOK_DEPTH,
	ROW_HEIGHT,
	ORDERBOOK_DIVIDER_HEIGHT_DESKTOP,
	ORDERBOOK_DIVIDER_HEIGHT_MOBILE,
	ORDERBOOK_ROW_GAP_MOBILE,
	ORDERBOOK_ROW_GAP_DESKTOP,
} from '../OrderbookConstants';
import { ORDERBOOK_DISPLAY_TYPE } from '../useOrderbookDisplayPreference';
import useOrderbookDisplayStore from 'src/stores/orderbook/useOrderbookDisplayStore';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';

const MIN_DEPTH = 1;
const MAX_DEPTH = MAX_ORDERBOOK_DEPTH;

/**
 * Hook to observe container height changes and calculate dynamic depth
 */
export const useDynamicOrderbookDepth = (
	displayType: ORDERBOOK_DISPLAY_TYPE,
	rowHeight = ROW_HEIGHT
) => {
	const [depth, setDepth] = useState<number>(MIN_DEPTH);
	const debounceTimeoutRef = useRef<Timer | undefined>(undefined);
	const isOrderbookVisible = useOrderbookDisplayStore(
		(s) => s.isDisplayingOrderbook
	);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const resizeObserverRef = useRef<ResizeObserver | null>(null);
	const isMobileScreenSize = useIsMobileScreenSize();

	/**
	 * Update the depth based on the container height.
	 * This is called when the container height changes.
	 * The area of the orderbook is calculated based on the height of the container.
	 */
	const updateDepth = useCallback(
		(containerHeight: number) => {
			const isVerticalOrderbook =
				displayType === ORDERBOOK_DISPLAY_TYPE.VERTICAL;

			const dividerHeight = isMobileScreenSize
				? ORDERBOOK_DIVIDER_HEIGHT_MOBILE
				: ORDERBOOK_DIVIDER_HEIGHT_DESKTOP;

			// From testing, dividing by 2 seems to allow for the most amount of rows,
			// while still keeping symmetry between the two sections when vertical.
			const rowGap =
				(isMobileScreenSize
					? ORDERBOOK_ROW_GAP_MOBILE
					: ORDERBOOK_ROW_GAP_DESKTOP) / (isVerticalOrderbook ? 2 : 1);

			const availableHeight =
				containerHeight - (isVerticalOrderbook ? dividerHeight : 0);

			// Each side gets half the available height
			const availableHeightPerSide = isVerticalOrderbook
				? availableHeight / 2
				: availableHeight;
			const calculatedDepth = Math.floor(
				availableHeightPerSide / (rowHeight + rowGap)
			);

			// Ensure minimum depth and reasonable maximum
			const newDepth = Math.max(
				Math.min(calculatedDepth, MAX_DEPTH),
				MIN_DEPTH
			);

			setDepth(newDepth);
		},
		[rowHeight, displayType, isMobileScreenSize]
	);

	/**
	 * Debounce the update depth function to prevent it from being called too often
	 */
	const debouncedUpdateHeight = useCallback<ResizeObserverCallback>(
		(entries) => {
			if (debounceTimeoutRef.current) {
				clearTimeout(debounceTimeoutRef.current);
			}

			const containerEntry = entries[0];

			if (!containerEntry) {
				return;
			}

			debounceTimeoutRef.current = setTimeout(() => {
				updateDepth(containerEntry.target.clientHeight);
			}, 8); // Debounce to ~120fps
		},
		[updateDepth]
	);

	const containerCallbackRef = useCallback<RefCallback<HTMLDivElement>>(
		(node: HTMLDivElement | null) => {
			if (!node || !isOrderbookVisible) {
				// Do any clean up if necessary.
				if (debounceTimeoutRef.current) {
					clearTimeout(debounceTimeoutRef.current);
				}

				if (resizeObserverRef.current && containerRef.current) {
					resizeObserverRef.current.unobserve(containerRef.current);
					resizeObserverRef.current.disconnect();
					resizeObserverRef.current = null;
					containerRef.current = null;
				}

				return;
			}

			containerRef.current = node;

			// Initial measurement
			updateDepth(node.clientHeight);

			// Set up ResizeObserver to track height changes
			const resizeObserver = new ResizeObserver(debouncedUpdateHeight);
			resizeObserver.observe(node);

			// Store for cleanup.
			resizeObserverRef.current = resizeObserver;
		},
		[updateDepth, debouncedUpdateHeight, isOrderbookVisible]
	);

	return { depth, containerCallbackRef };
};
